import type { ButtonProps as AriaButtonProps } from 'react-aria-components';

import { Plus } from '@untitledui/icons/Plus';
import { useState } from 'react';

import { Dialog, Modal, ModalOverlay } from '@/components/base/modals/modal';
import {
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
} from '@/components/base/tooltip/tooltip';
import { ProfilePictureUpload } from '@/components/profile-picture-upload/profile-picture-upload';
import { cx } from '@/utils/cx';

const sizes = {
  xs: { root: 'size-6', icon: 'size-4' },
  sm: { root: 'size-8', icon: 'size-4' },
  md: { root: 'size-10', icon: 'size-5' },
  lg: { root: 'size-12', icon: 'size-7' },
};

interface AvatarAddButtonProps extends AriaButtonProps {
  size: 'xs' | 'sm' | 'md' | 'lg';
  title?: string;
  className?: string;
  userId?: string;
  onUploadSuccess?: () => void;
}

export const AvatarAddButton = ({
  size,
  className,
  title = 'Add profile picture',
  userId,
  onUploadSuccess,
  ...props
}: AvatarAddButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <AriaTooltip title={title}>
        <AriaTooltipTrigger
          {...props}
          aria-label={title}
          onPress={() => {
            if (userId) {
              setIsModalOpen(true);
            }
          }}
          className={cx(
            'flex cursor-pointer items-center justify-center rounded-full border border-dashed border-primary bg-brand-50 text-fg-quaternary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2 disabled:border-gray-200 disabled:bg-secondary disabled:text-gray-200',
            sizes[size].root,
            className,
          )}
        >
          <Plus
            className={cx(
              'text-current transition-inherit-all',
              sizes[size].icon,
            )}
          />
        </AriaTooltipTrigger>
      </AriaTooltip>

      {userId && (
        <ModalOverlay
          centered
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
        >
          <Modal>
            <Dialog>
              <div className="w-full max-w-xl rounded-xl bg-primary p-6 shadow-xl">
                <ProfilePictureUpload
                  userId={userId}
                  onUploadSuccess={() => {
                    setIsModalOpen(false);
                    onUploadSuccess?.();
                  }}
                  onCancel={() => setIsModalOpen(false)}
                />
              </div>
            </Dialog>
          </Modal>
        </ModalOverlay>
      )}
    </>
  );
};
